import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading, false = unauth, object = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // Skip the /auth/me probe entirely if no token is stored — avoids a guaranteed
    // 401 on every public page load (cleaner browser console).
    if (!localStorage.getItem("bp_token")) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, captcha) => {
    try {
      const body = { email, password };
      if (captcha) {
        body.captcha_token = captcha.token;
        body.captcha_answer = captcha.answer;
      }
      const { data } = await api.post("/auth/login", body);
      if (data?.access_token) localStorage.setItem("bp_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("bp_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
